import { ComponentIds, Entity, ProductUtils, SelectedComponent } from '@inter-ikea-core/core';
import { Product, ProductType, SelectionOverrideComponent } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';
import { getMarketProducts, getSelectedGroupId } from 'ducks/products';
import { setShowSelectionOverrideIndicators } from 'ducks/selectionOverride';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isMobile } from 'utils/platform';
import InteriorSuggestionsDesktop from '../../shared/widgets/interiorSuggestions/InteriorSuggestionsDesktop';
import InteriorSuggestionsMobile from '../../shared/widgets/interiorSuggestions/InteriorSuggestionsMobile';
import addInteriorSuggestion from '../behaviors/addInteriorSuggestion';
import SelectInSceneImage from './images/InteriorSuggestionsSelectInScene.png';
import { store } from 'ducks';
import { useBehaviorTracker } from 'contexts/behaviors';
import { noneOptionForInteriorSuggestions } from 'rangeConfig/shared/widgets/interiorSuggestions/noneOptionForInteriorSuggestions';

const sectionTypes = [ProductType.IVAR_SECTION];
const interiorSuggestionTypes = [ProductType.IVAR_INTERIOR_SUGGESTION];

export default function InteriorSuggestions() {
  const dispatch = useAppDispatch();
  const rootState = store.getState();
  const products = useSelector(getMarketProducts);
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();
  const entityManager = coreApi.getServices().entityManager;
  const selectedGroupId = useSelector(getSelectedGroupId);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [interiorSuggestions, setInteriorSuggestions] = useState<Product[]>([]);

  const interiorSuggestionProducts = useMemo(() => {
    return products.filter(p => interiorSuggestionTypes.includes(p.type));
  }, [products]);

  const sectionProducts = useMemo(() => {
    return products.filter(p => sectionTypes.includes(p.type));
  }, [products]);

  useEffect(() => {
    if (selectedEntity) {
      const sectionProduct = sectionProducts.find(
        p => p.coreProductId === ProductUtils.productRef(selectedEntity)
      );

      const suggestionsForSelectedFrame = interiorSuggestionProducts.filter(p => {
        return sectionProduct
          ? p.communicatedDimensions.width === sectionProduct.communicatedDimensions.width &&
              p.communicatedDimensions.depth === sectionProduct.communicatedDimensions.depth &&
              p.communicatedDimensions.height === sectionProduct.communicatedDimensions.height
          : false;
      });
      setInteriorSuggestions(suggestionsForSelectedFrame);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  const addSelectionOverrideComponentToSections = useCallback(() => {
    const sectionsInScene = coreApi.entities.getByProductType(ProductType.IVAR_SECTION);
    sectionsInScene.forEach(section => section.addComponent(new SelectionOverrideComponent()));
    if (sectionsInScene.length === 1) {
      // If only one frame in scene, select it
      sectionsInScene[0].addComponent(new SelectedComponent());
      setSelectedEntity(sectionsInScene[0]);
    }
  }, [coreApi]);

  const removeSelectionOverrideComponentFromSections = useCallback(() => {
    const sectionsInScene = coreApi.entities.getByProductType(ProductType.IVAR_SECTION);
    sectionsInScene.forEach(section => section.removeComponent(SelectionOverrideComponent.id));
  }, [coreApi]);

  useEffect(() => {
    addSelectionOverrideComponentToSections();
    dispatch(setShowSelectionOverrideIndicators(true));
    return () => {
      removeSelectionOverrideComponentFromSections();
      dispatch(setShowSelectionOverrideIndicators(false));
      coreApi.entities.unselect();
    };
  }, [
    addSelectionOverrideComponentToSections,
    coreApi,
    dispatch,
    removeSelectionOverrideComponentFromSections
  ]);

  useEffect(() => {
    const onSelectedEntity = (entity: Entity) => {
      if (entity.hasComponent(SelectionOverrideComponent.id)) {
        setSelectedEntity(entity);
      }
    };
    const onUnSelectedEntity = (entity: Entity) => {
      if (entity.hasComponent(SelectionOverrideComponent.id)) {
        setSelectedEntity(null);
      }
    };

    entityManager.subscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, onSelectedEntity);
    entityManager.subscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, onUnSelectedEntity);
    return () => {
      entityManager.unsubscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, onSelectedEntity);
      entityManager.unsubscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, onUnSelectedEntity);
    };
  }, [entityManager]);

  const handleClick = (product: Product) =>
    behaviorTracker.custom(() => {
      const props = {
        coreProductId: product.coreProductId,
        menu: 'main_menu'
      };
      return addInteriorSuggestion({ coreApi, dispatch, rootState, props });
    });

  return isMobile() ? (
    <InteriorSuggestionsMobile
      selectedEntity={selectedEntity}
      selectedGroupId={selectedGroupId}
      suggestionsForSelectedFrame={interiorSuggestions}
      handleClick={handleClick}
      message="storageone.selectFrameInSceneChatBubbleMessageSections"
      image={SelectInSceneImage}
      noneOptionForInteriorSuggestions={noneOptionForInteriorSuggestions}
    />
  ) : (
    <InteriorSuggestionsDesktop
      selectedEntity={selectedEntity}
      selectedGroupId={selectedGroupId}
      suggestionsForSelectedFrame={interiorSuggestions}
      handleClick={handleClick}
      message="storageone.selectFrameInSceneChatBubbleMessageSections"
      image={SelectInSceneImage}
      noneOptionForInteriorSuggestions={noneOptionForInteriorSuggestions}
    />
  );
}
